/**
 * Intro request page - submit or view introduction requests.
 * 
 * Adapts based on registrationKind:
 * - Sponsors: Request introductions to candidates
 * - Friends: View received introduction requests
 * 
 * Requires sponsor_access (enforced by SponsorAccessRoute).
 * Friends are allowed through (see ProtectedRoute.tsx).
 * 
 * Safety doctrine:
 * - No direct contact details collected
 * - No legal name, phone, email, address
 * - No employer, income, net worth
 * - No social media handles
 * - Optional short message only
 * - Required acknowledgement checkbox
 * 
 * TODO(ACT 7): Emit intro_request_started audit event on page load
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIntroRequestProgram } from '@/features/intro-request/useIntroRequestProgram';
import { IntroRequestView } from '@/features/intro-request/view';
import { introRequestStyles } from '@/components/intro-request/introRequestStyles';
import { useAuthStore } from '@/store/authStore';

/**
 * Intro request page component.
 * 
 * Elm-ish architecture: This is a thin shell that:
 * - Reads URL parameters
 * - Initializes the program
 * - Renders the view
 * - Adapts copy based on registrationKind
 * 
 * All business logic is in the program hook.
 */
export function IntroRequestPage() {
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('cardId');
  const registrationKind = useAuthStore(state => state.registrationKind);
  const isFriend = registrationKind === 'friend';
  
  const { 
    model, 
    start, 
    editMessage, 
    toggleAcknowledgement, 
    submit, 
    retry, 
    back 
  } = useIntroRequestProgram();

  // Start the intro request flow when cardId is available
  useEffect(() => {
    start(cardId);
  }, [start, cardId]);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          {isFriend ? 'Introduction Requests' : 'Request Introduction'}
        </h1>
        {model.status === 'form' && (
          <p className="page-description">
            {isFriend
              ? 'View introduction requests from sponsors.'
              : 'Submit an introduction request for this candidate.'}
          </p>
        )}
      </div>
      
      <div className="intro-content" data-testid="intro-content">
        <IntroRequestView
          model={model}
          onStart={start}
          onMessageEdit={editMessage}
          onAcknowledgementToggle={toggleAcknowledgement}
          onSubmit={submit}
          onRetry={retry}
          onBack={back}
        />
      </div>
      
      <style>{introRequestStyles}</style>
    </div>
  );
}
